import type { LocaleDestinationRow } from '../types/gmc-connection.types';

/**
 * `config.locale_destinations` ⇄ form rows for the GMC delivery-languages field array. Each
 * entry maps one translated locale (en-GB, es-419, es-ES) to its own GMC destination — the
 * backend's `GmcLocaleDestination`; a locale with no entry is skipped at delivery. Pure so
 * the round-trip rules stay in one place.
 */

/** One stored entry, as the backend persists it (snake_case, values unvalidated here). */
type StoredLocaleDestination = Partial<
  Record<'feed_label' | 'datasource_id' | 'content_language' | 'sftp_file_name', unknown>
>;

/** Wire entry sent back in `config.locale_destinations`. */
export type LocaleDestinationConfig = Record<string, string>;

/** Stringify a stored scalar; anything else reads as blank. */
function text(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
}

/** The locale's country as a GMC feed label (en-GB → GB, es-ES → ES); `null` for a region
 *  that isn't a country (es-419 = Latin America), where the label is the merchant's call. */
export function regionLabel(locale: string): string | null {
  const region = locale.split('-')[1];
  return region && /^[A-Za-z]{2}$/.test(region) ? region.toUpperCase() : null;
}

/** Labels merchants commonly use for the same market. A feed label is free-form and matched
 *  EXACTLY (it is part of the product id `{lang}~{label}~{offer}`), so the ISO code is only a
 *  guess — e.g. many UK feeds are labelled `UK`, not `GB`. */
const LABEL_ALIASES: Record<string, readonly string[]> = { GB: ['UK'], UK: ['GB'] };

/**
 * The feed label to prefill for a locale. With the account's primary-feed labels known, only a
 * label a primary feed actually uses: the locale's country, else a known alias of it (GB → UK),
 * else nothing (the operator picks). Without them (create mode, list unavailable) the country.
 */
export function suggestFeedLabel(locale: string, primaryLabels: readonly string[] | null): string {
  const region = regionLabel(locale);
  if (!primaryLabels) return region ?? '';
  if (region && primaryLabels.includes(region)) return region;
  const alias = (region ? (LABEL_ALIASES[region] ?? []) : []).find((a) =>
    primaryLabels.includes(a),
  );
  return alias ?? '';
}

/**
 * One row per stored locale (en-US excluded — it is the English source), in stored order and
 * spelling. These are the field array's starting rows; "Add language" appends the rest.
 */
export function buildLocaleDestinationRows(
  config: Record<string, unknown> | undefined,
): LocaleDestinationRow[] {
  const raw = config?.locale_destinations;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return [];
  return Object.entries(raw as Record<string, StoredLocaleDestination>)
    .filter(([code]) => code.toLowerCase() !== 'en-us')
    .map(([code, entry]) => ({
      locale: code,
      feedLabel: text(entry.feed_label),
      datasourceId: text(entry.datasource_id),
      sftpFileName: text(entry.sftp_file_name),
      contentLanguage: text(entry.content_language),
    }));
}

/**
 * `config.locale_destinations` for the create/PATCH body. `undefined` = leave the stored value
 * alone: delivery is off, or the rows equal `baseline` (what was loaded) so the user changed
 * nothing. `null` = every language removed. Config shallow-merges server-side, so the whole
 * map is always sent.
 */
export function buildLocaleDestinationsConfig(
  rows: readonly LocaleDestinationRow[],
  {
    deliveryEnabled,
    baseline,
  }: { deliveryEnabled: boolean; baseline: readonly LocaleDestinationRow[] },
): Record<string, LocaleDestinationConfig> | null | undefined {
  if (!deliveryEnabled || JSON.stringify(rows) === JSON.stringify(baseline)) return undefined;

  const destinations: Record<string, LocaleDestinationConfig> = {};
  for (const row of rows) {
    const entry: LocaleDestinationConfig = {
      feed_label: row.feedLabel.trim(),
      datasource_id: row.datasourceId.trim(),
    };
    const contentLanguage = row.contentLanguage?.trim();
    if (contentLanguage) entry.content_language = contentLanguage;
    const sftpFileName = row.sftpFileName?.trim();
    if (sftpFileName) entry.sftp_file_name = sftpFileName;
    destinations[row.locale] = entry;
  }
  return Object.keys(destinations).length > 0 ? destinations : null;
}
